import { useState, useCallback, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { PlusIcon, GripIcon, TrashIcon, PrayIcon } from '@components/icons';
import { EmptyState } from '@components/common';

/**
 * Prayer tab content with drag-and-drop reordering
 */
export const PrayerContent = observer(function PrayerContent() {
  const [newPrayer, setNewPrayer] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const prayerItems = bibleState.prayerItems;

  // Focus input when adding
  useEffect(() => {
    if (isAdding) {
      inputRef.current?.focus();
    }
  }, [isAdding]);

  const handleAdd = useCallback(() => {
    if (!newPrayer.trim()) return;
    bibleState.addPrayerItem(newPrayer.trim());
    setNewPrayer('');
    setIsAdding(false);
  }, [newPrayer]);

  const handleDelete = useCallback((id: string) => {
    bibleState.showConfirmDialog({
      title: 'Delete Prayer',
      message: 'Delete this prayer item?',
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: () => bibleState.removePrayerItem(id),
    });
  }, []);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleAdd();
      } else if (e.key === 'Escape') {
        setIsAdding(false);
        setNewPrayer('');
      }
    },
    [handleAdd],
  );

  // Drag and drop handlers
  const handleDragStart = useCallback((e: React.DragEvent, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    setTimeout(() => {
      (e.target as HTMLElement).style.opacity = '0.5';
    }, 0);
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    (e.target as HTMLElement).style.opacity = '1';
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, id: string) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (id !== draggedId) {
        setDragOverId(id);
      }
    },
    [draggedId],
  );

  const handleDragLeave = useCallback(() => {
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetId: string) => {
      e.preventDefault();
      if (!draggedId || draggedId === targetId) {
        setDragOverId(null);
        return;
      }

      const items = [...prayerItems];
      const draggedIndex = items.findIndex((item) => item.id === draggedId);
      const targetIndex = items.findIndex((item) => item.id === targetId);

      if (draggedIndex === -1 || targetIndex === -1) return;

      const [draggedItem] = items.splice(draggedIndex, 1);
      items.splice(targetIndex, 0, draggedItem);

      const reorderedItems = items.map((item, index) => ({
        ...item,
        order: index,
      }));

      bibleState.reorderPrayerItems(reorderedItems);
      setDragOverId(null);
    },
    [draggedId, prayerItems],
  );

  return (
    <>
      {/* Add button / form */}
      {isAdding ? (
        <div className="mb-3 flex flex-col gap-2">
          <input
            ref={inputRef}
            type="text"
            value={newPrayer}
            onChange={(e) => setNewPrayer(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Prayer request..."
            className="bookmark-input w-full"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setIsAdding(false);
                setNewPrayer('');
              }}
              className="rounded-xl bg-stone-200/50 px-4 py-2 text-stone-600 transition-all hover:bg-stone-300/50 dark:bg-stone-700/50 dark:text-stone-400 dark:hover:bg-stone-600/50"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              className="rounded-xl bg-cyan-600 px-5 py-2 text-white transition-all hover:bg-cyan-700 dark:bg-cyan-500 dark:hover:bg-cyan-400"
            >
              Add
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          className="mb-3 flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-100/50 py-2 text-cyan-700 transition-all hover:bg-cyan-200/70 dark:bg-cyan-900/30 dark:text-cyan-300 dark:hover:bg-cyan-800/40"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Prayer</span>
        </button>
      )}

      {/* Prayer list */}
      {prayerItems.length === 0 ? (
        <EmptyState
          icon={<PrayIcon className="h-10 w-10" />}
          title="No prayer items yet"
          hint="Tap Add Prayer above to start your prayer list"
        />
      ) : (
        prayerItems.map((item) => (
          <div
            key={item.id}
            className={`prayer-row ${dragOverId === item.id ? 'drag-over' : ''} ${draggedId === item.id ? 'dragging' : ''}`}
            draggable
            onDragStart={(e) => handleDragStart(e, item.id)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, item.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, item.id)}
          >
            <div className="prayer-drag-handle" title="Drag to reorder">
              <GripIcon className="h-4 w-4" />
            </div>
            <p className="prayer-text">{item.description}</p>
            <button
              onClick={() => handleDelete(item.id)}
              className="bookmark-action-btn flex-shrink-0 hover:text-red-600 dark:hover:text-red-400"
              title="Delete"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        ))
      )}
    </>
  );
});
